import { Browser } from "../core/browser";
import { Dialog } from "../dialog/dialog";

let UnloadInitialized = false;

export class Ajax {
	static DefaultOptions = {
		method: "GET",
		headers: {},
		preventSlidingExpiration: false,
		processData: true,
		ResponseOverrides: {},
		LoadTimeout: 2000,
		error: error => Dialog.ShowError(Localizer.ERROR_TITLE, Localizer.ERROR_MESSAGE, error)
	}

	static async #Send(url, options) {
		url = Browser.ResolveRelativeURL(url);

		let preventTimeout = false,
			abortController = new AbortController();

		try {
			const config = {
				...this.DefaultOptions,
				url,
				queryParameters: options.queryParameters ?? {},
				method: options.method,
				headers: {
					'RequestVerificationToken': document.querySelector("input[name='__RequestVerificationToken']")?.value,
					...options.headers
				},
				body: options.body,
				signal: abortController.signal
			};

			options = { ...this.DefaultOptions, ...options };

			if (options.preventSlidingExpiration) { config.headers["x-prevent-sliding-expiration"] = true; }

			if (options.LoadTimeout) {
				setTimeout(() => {
					if (!preventTimeout) {
						Ajax.#ShowThrobber();
					}
				}, options.LoadTimeout);
			}

			if (options.OnBeforeSend) {
				await options.OnBeforeSend(config);
			}

			if (!(options.body instanceof HTMLFormElement)) {
				const sendAsJSON = config.body?.constructor.name === "Object" || Array.isArray(config.body);

				if (sendAsJSON) {
					config.headers['Content-Type'] = 'application/json';
					config.body = JSON.stringify(config.body);
				}
			} else {
				config.body = new FormData(options.body);
				config.body.delete("__RequestVerificationToken");
			}

			const parsedURL = new URL(config.url, location);
			parsedURL.hash = "";

			Object.entries(config.queryParameters ?? {}).forEach(([key, value]) => {
				if (!Array.isArray(config.queryParameters[key])) {
					parsedURL.searchParams.set(key, value);
				} else {
					config.queryParameters[key].forEach(item => parsedURL.searchParams.append(key, item));
				}
			});

			if (options.OnCheckValid?.(config) !== false) {
				const fetchResponse = await fetch(parsedURL.toString(), config),
					{ headers, ok, redirected, status, statusText, type } = fetchResponse;

				const content = options.processData ? await fetchResponse.text() : "";

				const response = {
					content,
					blob: () => fetchResponse.blob(),
					json: () => fetchResponse.json(),
					body: options.processData ? Browser.TryParseJSON(content) : fetchResponse.body,
					url: fetchResponse.url,
					headers, ok, redirected, status, statusText, type
				};

				options.OnComplete?.(response);
				preventTimeout = true;

				Ajax.HandleResponse(response, options.success);

				return response;
			} else {
				Ajax.#HideThrobber();
				preventTimeout = true;

				options.OnComplete?.();
			}
		} catch (error) {
			Ajax.#HideThrobber();
			preventTimeout = true;

			if (!UnloadInitialized) {
				options.error?.(error);
			}
		}
	}

	static Get = async (url, options) => await this.#Send(url, { ...this.DefaultOptions, ...options, method: "GET" });
	static Post = async (url, options) => await this.#Send(url, { ...this.DefaultOptions, ...options, method: "POST" });
	static Put = async (url, options) => await this.#Send(url, { ...this.DefaultOptions, ...options, method: "PUT" });
	static Delete = async (url, options) => await this.#Send(url, { ...this.DefaultOptions, ...options, method: "DELETE" });
	static Patch = async (url, options) => await this.#Send(url, { ...this.DefaultOptions, ...options, method: "PATCH" });

	static HandleResponse(response, overrides = {}) {
		Ajax.#HideThrobber();

		overrides = { ...this.DefaultOptions.ResponseOverrides, ...overrides };
		overrides.any?.(response);

		const func = response.ok ? overrides.ok : overrides[response.status];

		if (typeof func !== "function") {
			switch (response.status) {
				case 400:
					console.log(response);
					return Dialog.ShowError(Localizer.ERROR_TITLE, Localizer.BAD_REQUEST);

				case 404:
					console.log(response);
					return Dialog.ShowError(Localizer.ERROR_TITLE, Localizer.NOT_FOUND);
				case 403: return Dialog.ShowError(Localizer.ACCESS_DENIED_TITLE, Localizer.ACCESS_DENIED);
				case 500: return Dialog.ShowError(Localizer.ERROR_TITLE, Localizer.ERROR_MESSAGE);

				default:
					if (response.ok && response.redirected) {
						location.href = response.url;
					}
					break;
			}
		} else {
			return func(response);
		}
	}

	static #ShowThrobber() {
		document.body.classList.add("blur");

		document.body.appendChild(
			document.createElementWithContents("div", "<div class='cubes'><div class='cube'></div><div class='cube'></div><div class='cube'></div><div class='cube'></div><div class='cube'></div></div>", { className: "ajax-throbber" })
		);
	}

	static #HideThrobber() {
		document.body.removeClass("blur");
		document.querySelector(".ajax-throbber")?.remove();
	}
}

window.addEventListener("beforeunload", () => UnloadInitialized = true);